// Copyright The Linux Foundation and each contributor to LFX.
// SPDX-License-Identifier: MIT
import { HttpService } from './http.service';
import { HttpClient } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { map, tap } from 'rxjs/operators';

import { API_KEY } from '@app/core/environment.configuration';

import {
  Policy,
  PolicyDraft,
  StatementService,
  ResourceAction,
  PolicyListResponse,
  PolicyDetailResponse
} from '@app/core/models';
import { PolicyMockSimple } from '../../access-management/core/services/policies.mock';

@Injectable()
export class PolicyService {
  constructor(
    private httpClient: HttpClient,
    private httpService: HttpService,
    @Inject(API_KEY) private api: string) {}

  public create(policy: PolicyDraft) {
    const route = `${this.api}/policies`;
    return this.httpClient.post(route, policy);
  }

  public getList(): Observable<Policy[]> {
    const route = `${this.api}/policies`;
    return this.httpClient
      .get<PolicyListResponse>(route)
      .pipe(map((response: PolicyListResponse) => response.policies));
  }

  public getServices(): Observable<StatementService[]> {
    const route = `${this.api}/policies/services`;
    return this.httpClient.get<StatementService[]>(route);
  }

  public getResourceActions(serviceId: string): Observable<ResourceAction[]> {
    const route = `${this.api}/policies/resource-actions/${serviceId}`;
    return this.httpClient.get<ResourceAction[]>(route);
  }

  public delete(policyId: string) {
    const route = `${this.api}/policies/${policyId}`;
    return this.httpClient.delete(route);
  }

  public update(policyId: string, policy: PolicyDraft) {
    const route = `${this.api}/policies/${policyId}`;
    return this.httpClient.put(route, policy).pipe(map((response: any) => true));
  }

  public updatePolicy(policyId: string, request: UpdatePolicyRequest) {
    const route = `${this.api}/policies/${policyId}`;
    return this.httpClient.put(route, request);
  }

  public get(policyId: string) {
    const route = `${this.api}/policies/${policyId}`;
    return this.httpClient.get<PolicyDetailResponse>(route).pipe(map(response => new PolicyDetailModel(response)));
  }

  public deleteStatement(policyId: string, statementId: string) {
    return this.httpClient.delete(`${this.api}/policies/${policyId}/statements/${statementId}`);
  }

  public createStatement(policyId: string, request: CreateStatementRequest) {
    const route = `${this.api}/policies/${policyId}/statements`;
    return this.httpClient.post(route, request);
  }

  public updateStatement(policyId: string, request: CreateStatementRequest) {
    const route = `${this.api}/policies/${policyId}/statements/${request.statement_id}`;
    return this.httpClient.put(route, request);
  }
}

export interface CreateStatementRequest  {
  statement_id?: string;
  effect: string;
  resource_id: string;
  actions: string[];
}

export interface UpdatePolicyRequest {
  name: string;
  description: string;
}


class PolicyDetailModel {
  statement: any = [];
  id: string;
  name: string;

  constructor(data: PolicyDetailResponse) {
    Object.assign(this, { ...data });

    this.id = data.policy_id;
    this.name = data.policy_name;

    // Serialize what we need from backend
    this.statement = data.statement && data.statement.map(stmt => ({
      ...stmt,

      // Serialize as ResourceActions
      actions: stmt.actions.map((action: any) =>
        ({id: action.action_id, name: action.name})),

      // Used in Statement table
      action: stmt.actions.map((item) => item.name).join(' ,'),

      // Used in Policy Statement Card
      resource: {
        id: stmt.resource_id,
        name: stmt.resource_name
      },
      service: {
        id: stmt.service_id,
        name: stmt.service_name
      }
    })) || [];
  }
}



// WEBPACK FOOTER //
// ./src/app/core/services/policy.service.ts